/**
 * Chapter landing — renders the chapter's landing Page (parts-driven) when
 * one exists. Falls back to the existing <HomePage/> visuals for chapter
 * '96' so the live reunion site stays intact until the seed is migrated.
 */

import { useEffect, useState } from 'react';
import { useScope } from '@/contexts/ScopeContext';
import { HomePage } from '@/pages/public/HomePage';
import { ApiClientError, pagesApi, type PageDetail } from '@/services/api';
import { PageRenderer } from '@/components/pages/PageRenderer';

export function ChapterLandingPage() {
  const { chapter, loading, error } = useScope();
  const [landing, setLanding] = useState<PageDetail | null>(null);
  const [landingLoading, setLandingLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!chapter?.landingPageId) {
      setLanding(null);
      return;
    }
    setLandingLoading(true);
    pagesApi
      .getById(chapter.landingPageId)
      .then((p) => {
        if (!cancelled) setLanding(p);
      })
      .catch((err: unknown) => {
        // Treat any failure as "no parts-driven landing yet" and fall back.
        if (!cancelled) setLanding(null);
        if (err instanceof ApiClientError) {
          // Quiet — fallback path handles this.
        }
      })
      .finally(() => {
        if (!cancelled) setLandingLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [chapter?.landingPageId]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12 text-center text-white/60">
        Loading chapter…
      </div>
    );
  }
  if (error || !chapter) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <h1 className="text-3xl font-display text-white mb-3">Chapter not found</h1>
        <p className="text-white/60">{error}</p>
      </div>
    );
  }

  // Parts-driven landing — use it when present and non-empty.
  if (landing && landing.parts.length > 0) {
    return <PageRenderer page={landing} />;
  }

  // Loading state for landing page detail.
  if (landingLoading) {
    return (
      <div className="container mx-auto px-4 py-12 text-center text-white/60">
        Loading chapter landing…
      </div>
    );
  }

  // Visual-parity fallback for chapter 96 until the seed is migrated.
  if (chapter.slug === '96') {
    return <HomePage />;
  }

  return (
    <div className="container mx-auto px-4 py-16">
      <h1 className="text-5xl font-display font-bold text-gradient mb-4">
        {chapter.displayName}
      </h1>
      <p className="text-white/70 text-lg">
        Chapter <span className="font-mono">{chapter.slug}</span>
        {chapter.batchYear ? ` — Class of ${chapter.batchYear}` : ''}.
      </p>
      <p className="text-white/50 mt-6">
        No landing page has been published for this chapter yet.
      </p>
    </div>
  );
}

export default ChapterLandingPage;
